// The season-by-season report archive: the last 12 reports in full (narrated, with deaths'
// contributing factors, the fragility breakdown and production factors), older seasons as
// summaries (24.7).
import type { ArchiveFull, ArchiveSummary, HistoryVM } from '../../viewModels';
import { factorTooltip } from '../../viewModels';
import { humanize, seasonName } from '../format';
import { causeLabel, uiText } from '../text/en';
import { narrationLine } from '../shell/text';
import { FactorList } from '../tooltip/factor-list';
import { Num } from '../tooltip/num';

function Full({ r }: { r: ArchiveFull }) {
  const t = uiText.history;
  return (
    <details class="archive__item">
      <summary>
        <span class="name">
          {seasonName(r.season)}, {uiText.header.year} {r.year}
        </span>{' '}
        <span class="hint">
          {uiText.report.population(r.population.start, r.population.end)} {t.fragility}{' '}
          <span class="num">{Math.round(r.fragility)}</span>
        </span>
      </summary>
      {r.deaths.length > 0 && (
        <ul class="prose-list">
          {r.deaths.map((d) => (
            <li key={d.personId}>
              {uiText.report.death(d.name, d.ageYears, d.illnessId === null ? causeLabel[d.cause] : humanize(d.illnessId), d.jobLabel.toLowerCase())}
              {d.factors.length > 0 && (
                <span class="hint">
                  {' '}
                  {uiText.report.factors}: {d.factors.map((f) => f.note ?? humanize(f.source)).join('; ')}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
      <ul class="prose-list">
        {r.lines.map((n, i) => (
          <li key={i}>{narrationLine(n)}</li>
        ))}
      </ul>
      {r.production.length > 0 && (
        <details class="archive__factors">
          <summary>{t.productionFactors}</summary>
          <ul class="prose-list">
            {r.production.map((p, i) => (
              <li key={i}>
                {narrationLine(p.narration)}
                {p.factors.length > 0 && <FactorList vm={factorTooltip(0, p.factors)} showResult={false} />}
              </li>
            ))}
          </ul>
        </details>
      )}
      <p class="hint">
        {t.fragility}: <Num value={r.fragility} format="whole" factors={r.fragilityFactors} label={t.fragility} />
      </p>
    </details>
  );
}

function Summary({ r }: { r: ArchiveSummary }) {
  return (
    <div class="archive__item archive__item--summary">
      <span class="name">
        {seasonName(r.season)}, {uiText.header.year} {r.year}
      </span>{' '}
      <span class="hint">{uiText.history.summary(r.population, r.births, r.deaths, r.events)}</span>
      {r.keyLines.length > 0 && (
        <ul class="prose-list">
          {r.keyLines.map((n, i) => (
            <li key={i}>{narrationLine(n)}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function Archive({ vm }: { vm: HistoryVM }) {
  return (
    <section aria-labelledby="archive-title">
      <h2 id="archive-title">{uiText.history.archive}</h2>
      <div class="archive">
        {vm.archive.map((r) =>
          r.kind === 'full' ? <Full key={r.turn} r={r} /> : <Summary key={r.turn} r={r} />,
        )}
      </div>
    </section>
  );
}
